import { useEffect } from 'react';

/**
 * Prints the page in the light theme, whatever theme the reader is using.
 *
 * A document that leaves the building (the printed BOL) is ink on white paper.
 * The theme lives on `<html data-theme>`, and the light remap is a long list of
 * `:root[data-theme="light"] …` rules in theme.css, so restating it inside a
 * print media query would be a second copy that drifts. Instead the attribute is
 * flipped for the print and put back afterwards. `beforeprint` fires for the
 * Print button's `window.print()` and for the browser's own Print command alike.
 *
 * The attribute is set directly, not through ThemeContext, so nothing is saved:
 * the reader's own theme choice is untouched.
 */
export function usePrintInLightTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    let saved: string | null = null;
    let flipped = false;

    const before = () => {
      saved = root.getAttribute('data-theme');
      flipped = true;
      root.setAttribute('data-theme', 'light');
    };
    const after = () => {
      if (!flipped) return;
      flipped = false;
      if (saved === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', saved);
    };

    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      after();
    };
  }, []);
}
